import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Barbell, ForkKnife, Snowflake, Target, UsersThree, Lightning, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const HERO_BG = "https://images.unsplash.com/photo-1676655079738-af54dfd6318e?crop=entropy&cs=srgb&fm=jpg&q=85";
const GYM_IMG = "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&q=85";
const ICE_IMG = "https://images.unsplash.com/photo-1548236339-55676e0726e0?crop=entropy&cs=srgb&fm=jpg&q=85";

const FEATURES = [
  { icon: Barbell, title: "AI Gym Plans", body: "Position-specific programming that closes the gap between where you play and where you want to play." },
  { icon: ForkKnife, title: "Fuel Plans", body: "Precise macros calibrated to your training load, body composition goal, and dietary preferences." },
  { icon: Snowflake, title: "Recovery Protocols", body: "Sleep, ice baths, mobility, and nutrition timing tactics used by elite programs." },
  { icon: Target, title: "Goal System", body: "Set targets for the levels above yours. Track lifts, sprints, and body metrics." },
  { icon: UsersThree, title: "Coach Access", body: "Elite tier lets your coach see everything and drop guidance directly into your feed." },
  { icon: Lightning, title: "Match-Ready", body: "Deload weeks, taper protocols and match-week templates built into every plan." },
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-[#262626] bg-[#0A0A0A]/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="landing-logo">
            <span className="font-heading font-black text-2xl uppercase">Gainline</span>
            <span className="font-heading font-black text-2xl text-[#FF3B30] uppercase">Performance</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-heading uppercase tracking-widest">
            <a href="#features" className="text-[#A3A3A3] hover:text-white">Features</a>
            <a href="#positions" className="text-[#A3A3A3] hover:text-white">Positions</a>
            <Link to="/pricing" className="text-[#A3A3A3] hover:text-white" data-testid="nav-pricing">Pricing</Link>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button
                onClick={() => nav(user.role === "coach" ? "/coach" : "/dashboard")}
                data-testid="nav-dashboard"
                className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-wider font-bold"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-sm font-heading uppercase tracking-widest text-[#A3A3A3] hover:text-white" data-testid="nav-login">
                  Sign In
                </Link>
                <Button
                  onClick={() => nav("/register")}
                  data-testid="nav-register"
                  className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-wider font-bold"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div className="fade-in">
            <div className="inline-flex items-center gap-2 border border-[#FF3B30] px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 bg-[#FF3B30]" />
              <span className="font-heading uppercase tracking-widest text-xs text-[#FF3B30]">Rugby · League · AFL · Soccer</span>
            </div>
            <h1 className="font-heading font-black uppercase text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
              Cross The<br />
              Gainline<span className="text-[#FF3B30]">.</span><br />
              Every Time<span className="text-[#FF3B30]">.</span>
            </h1>
            <p className="mt-8 text-lg text-[#A3A3A3] max-w-md">
              AI-generated gym and fuel plans tailored to your sport, position, and where you're aiming to be. Match-week tapering and recovery science built in.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                onClick={() => nav("/register")}
                data-testid="hero-cta-start"
                className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-wider font-bold text-base px-8 py-6"
              >
                Start Training <ArrowRight className="ml-2" size={20} weight="bold" />
              </Button>
              <Button
                variant="outline"
                onClick={() => nav("/pricing")}
                data-testid="hero-cta-pricing"
                className="rounded-none border-white text-white bg-transparent hover:bg-white hover:text-black font-heading uppercase tracking-wider font-bold text-base px-8 py-6"
              >
                See Plans
              </Button>
            </div>
          </div>
          <div className="hidden md:grid grid-cols-2 gap-4 fade-in stagger-2">
            <div className="border border-[#262626] p-6 bg-[#0A0A0A]/60 backdrop-blur">
              <div className="font-heading font-black text-5xl">4</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-[#A3A3A3]">Sports Supported</div>
            </div>
            <div className="border border-[#FF3B30] p-6 bg-[#FF3B30]/10">
              <div className="font-heading font-black text-5xl text-[#FF3B30]">AI</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-white">Personalised</div>
            </div>
            <div className="border border-[#262626] p-6 bg-[#0A0A0A]/60 backdrop-blur">
              <div className="font-heading font-black text-5xl">4x</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-[#A3A3A3]">Weekly Sessions</div>
            </div>
            <div className="border border-[#262626] p-6 bg-[#0A0A0A]/60 backdrop-blur">
              <div className="font-heading font-black text-5xl">24/7</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-[#A3A3A3]">Coach Sync</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-t border-[#262626] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30] mb-3">/ The System</div>
            <h2 className="font-heading font-black uppercase text-5xl md:text-6xl leading-tight">
              Every Rep<br /><span className="text-[#FF3B30]">Engineered.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="border border-[#262626] p-8 bg-[#121212] hover:border-[#FF3B30] transition-colors group" data-testid={`feature-${i}`}>
                  <Icon size={36} weight="duotone" className="text-[#FF3B30] mb-6" />
                  <h3 className="font-heading font-bold uppercase text-2xl mb-3">{f.title}</h3>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* POSITIONS */}
      <section id="positions" className="relative border-t border-[#262626] py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(${GYM_IMG})` }}
        />
        <div className="relative max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30] mb-3">/ Sport-Aware</div>
            <h2 className="font-heading font-black uppercase text-5xl md:text-6xl leading-tight">
              Built For<br />Your Code.
            </h2>
            <p className="mt-6 text-[#A3A3A3] max-w-md">
              A Prop trains differently than a Fly-half. A Striker trains differently than a Centre-Back. Our AI knows the difference and builds plans that reflect the demands of your sport, position, and level.
            </p>
          </div>
          <div className="space-y-4">
            {[
              { code: "Rugby Union", tags: ["Prop", "Fly-half", "Wing", "+7"] },
              { code: "Rugby League", tags: ["Hooker", "Halfback", "Fullback", "+6"] },
              { code: "AFL", tags: ["Ruckman", "Midfielder", "Key Forward", "+6"] },
              { code: "Soccer", tags: ["Striker", "CDM", "Centre-Back", "+7"] },
            ].map((s) => (
              <div key={s.code} className="border border-[#262626] bg-[#121212] p-4 flex items-center justify-between" data-testid={`sport-${s.code}`}>
                <div className="font-heading font-black uppercase text-xl">{s.code}</div>
                <div className="flex gap-2">
                  {s.tags.map((t) => (
                    <span key={t} className="border border-[#262626] px-2 py-0.5 text-xs uppercase font-heading tracking-widest text-[#A3A3A3]">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECOVERY */}
      <section className="border-t border-[#262626] py-24 grid md:grid-cols-2">
        <div className="relative min-h-[400px]">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${ICE_IMG})` }} />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="p-12 md:p-16 flex flex-col justify-center bg-[#0A0A0A]">
          <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30] mb-3">/ Recovery Ops</div>
          <h2 className="font-heading font-black uppercase text-5xl leading-tight mb-6">
            The Work<br />Between<br />Sessions.
          </h2>
          <p className="text-[#A3A3A3] max-w-md">
            Sleep protocols, contrast therapy, hydration math, deload weeks — the invisible work that separates first XV from second string.
          </p>
          <Link to="/pricing" className="mt-8 inline-flex items-center gap-2 font-heading uppercase tracking-widest text-sm text-[#FF3B30] hover:text-white" data-testid="recovery-cta">
            Unlock Recovery <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#262626] py-24 bg-[#FF3B30]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-black uppercase text-5xl md:text-7xl leading-none text-black">
            Ready to<br />Level Up?
          </h2>
          <Button
            onClick={() => nav("/register")}
            data-testid="cta-bottom-start"
            className="mt-10 rounded-none bg-black hover:bg-[#0A0A0A] text-white font-heading uppercase tracking-widest font-bold text-base px-10 py-6"
          >
            Start Free <ArrowRight className="ml-2" size={20} weight="bold" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-[#262626] py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-4">
          <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">© 2026 Gainline Performance. Built for the collision codes.</div>
          <div className="flex gap-6 text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
